import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLine } from '../aiAccess/shared';
import type { Coverage, EnrichmentClient, ProductLookup } from './api';
import { buildTimeline } from './timeline';
import { useSerialLookup } from './SerialLookup';
import {
  attentionBadge,
  oemName,
  productTitle,
  shortDate,
  sourceHost,
  warrantyBadge,
} from './format';
import { Backdrop, Badge, FIELD, Icon, LABEL, LINK, MONO, buttonClass, useDialog, useElapsed } from './ui';

/** Service levels shown before "+N more". */
const LEVELS_SHOWN = 4;

/** A coverage's bar (and legend swatch), by status; expired stays a quiet grey in dark too. */
const BAR_COLOURS: Record<string, string> = {
  active: 'bg-[#10B981]',
  expired: 'bg-[#CBD5E1] dark:bg-[#484f58]',
  not_started: 'bg-[#A7F3D0] dark:bg-[#10B981]/40',
};

/** The drawer's headline badge for a stored unit. */
const HEADLINE: Record<string, string> = {
  active: 'Under warranty',
  expired: 'Expired',
  not_started: 'Not started',
};

/**
 * DOC 2: one serial, on the right. A stored unit: product and OEM, a summary, the coverage
 * timeline and each coverage; a serial that is not stored (the OEM did not know it, needs the model
 * number, or the check failed): why, and asking again. The footer says when it was checked and asks
 * the OEM again (refresh). `answer` is shown as it is; without it the stored record is read.
 */
export function SerialDrawer({ client, serial, answer, onClose, onStored, fallbackFocus }: {
  readonly client: EnrichmentClient | null;
  readonly serial: string;
  readonly answer: ProductLookup | null;
  readonly onClose: () => void;
  /** The OEM was asked: the list and tiles may have changed. */
  readonly onStored: () => void;
  /** Where focus goes on close when the control that opened it is gone. */
  readonly fallbackFocus?: RefObject<HTMLElement>;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<ProductLookup | null>(answer);
  const [loading, setLoading] = useState(!answer);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [model, setModel] = useState('');
  const lookup = useSerialLookup(client);
  const asking = lookup.state.phase === 'checking';
  const elapsed = useElapsed(asking);
  const dialogRef = useRef<HTMLElement>(null);
  const headingId = useId();
  const { onKeyDown } = useDialog(dialogRef, onClose, true, fallbackFocus);
  const tRef = useRef(t);
  tRef.current = t;

  // The stored record, read once (an answer given to the drawer is shown as it is).
  useEffect(() => {
    if (answer || !client) return undefined;
    const request = new AbortController();
    setLoading(true);
    client.product(serial, request.signal)
      .then((next) => { if (!request.signal.aborted) setData(next); })
      .catch((e) => {
        if (request.signal.aborted) return;
        const text = tRef.current;
        setError((e as { status?: number }).status === 404 ? text('Nothing is stored for this serial.') : (e as Error).message || text('Something went wrong.'));
      })
      .finally(() => { if (!request.signal.aborted) setLoading(false); });
    return () => request.abort();
  }, [answer, client, serial]);

  const oem = oemName(data?.oem) ?? oemName(data?.check?.detection?.oem ?? null);
  const askAgain = async (modelNumber?: string) => {
    setNote(null);
    const code = data?.oem?.code ?? data?.check?.detection?.oem ?? null;
    const next = await lookup.ask({
      serial_number: data?.serial_number ?? serial,
      ...(code ? { oem: code } : {}),
      ...(modelNumber ? { model_number: modelNumber } : { refresh: true }),
    });
    if (!next) return;
    onStored();
    if (next.found || !data?.found) {
      setData(next);
      setModel('');
    } else {
      // The stored unit stays; say what the OEM answered this time.
      setNote(t('{{oem}} did not confirm it this time ({{status}}); the stored answer stays.', { oem: oem ?? t('The OEM'), status: next.status ?? '' }));
    }
  };

  const shownSerial = data?.serial_number ?? serial;
  const stored = Boolean(data?.found);
  const status = data?.warranty?.status ?? 'unknown';
  const attention = data && !stored ? attentionBadge(data.status) : null;
  let title = productTitle(data?.product) ?? shownSerial;
  if (data && !stored) {
    if (data.status === 'needs_more_info') title = t('{{oem}} needs the model number', { oem: oem ?? t('The OEM') });
    else if (data.status === 'error') title = t('The last check failed');
    else title = t('{{oem}} does not know this serial', { oem: oem ?? t('The OEM') });
  }

  return (
    <>
      <Backdrop onClick={onClose} />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="fixed bottom-0 right-0 top-0 z-[61] flex w-[600px] max-w-full flex-col border-l border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-[#0B1B3D] dark:text-[#e6edf3] shadow-[-8px_0_32px_rgba(11,27,61,0.10)] outline-none"
      >
        <div className="flex items-start gap-3 border-b border-[#E5E7EB] dark:border-[#30363d] px-6 pb-[18px] pt-6">
          <div className="min-w-0 flex-1">
            {data && (
              <div className="mb-2.5 flex flex-wrap gap-1.5">
                {oem && <Badge tone="neutral">{oem}</Badge>}
                {stored
                  ? <Badge tone={warrantyBadge(status).tone}>{t(HEADLINE[status] ?? 'No coverage')}</Badge>
                  : attention && <Badge tone={attention.tone}>{t(attention.label)}</Badge>}
              </div>
            )}
            <h2 id={headingId} className="m-0 mb-1.5 text-[19px] font-extrabold leading-[1.3] tracking-[-0.02em]">{title}</h2>
            <div className="flex items-center gap-2 text-[13px] text-[#5C6B85] dark:text-[#8b949e]">
              <span>{t('Serial')}</span>
              <span className={`${MONO} font-medium text-[#0B1B3D] dark:text-[#e6edf3]`}>{shownSerial}</span>
              <CopyButton text={shownSerial} />
            </div>
          </div>
          <button
            type="button"
            aria-label={t('Close')}
            onClick={onClose}
            className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-[#434A59] dark:text-[#c9d1d9] hover:bg-[#F7F8FA] dark:hover:bg-[#161b22]"
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-5">
          {loading && <p className="m-0 text-sm text-[#5C6B85] dark:text-[#8b949e]">{t('Loading…')}</p>}
          <ErrorLine message={error} />
          {data?.stale && (
            <p role="status" className="m-0 rounded-[10px] bg-[#FEF3C7] dark:bg-[#F59E0B]/15 px-3.5 py-3 text-[13px] text-[#92400E] dark:text-[#FBBF24]">
              {t('{{oem}} could not be asked just now, so this is the stored answer.', { oem: oem ?? t('The OEM') })}
              {data.refresh_error?.message ? ` ${data.refresh_error.message}` : ''}
            </p>
          )}
          {data && stored && <StoredUnit data={data} oem={oem} />}
          {data && !stored && (
            <NotStored
              data={data}
              oem={oem}
              model={model}
              onModel={setModel}
              onAsk={(value) => void askAgain(value)}
              busy={asking}
            />
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] px-6 py-3.5">
          <div className="min-w-0 flex-1 text-xs leading-normal text-[#5C6B85] dark:text-[#8b949e]" aria-live="polite">
            {asking ? (
              <>
                <strong className="text-[#0B1B3D] dark:text-[#e6edf3]">{t('Asking {{oem}} again…', { oem: oem ?? t('the OEM') })}</strong> {t('{{seconds}} s', { seconds: elapsed })}
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]">
                  <div className="h-1 rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3] transition-[width] duration-1000" style={{ width: `${Math.min(95, 5 + elapsed * 4)}%` }} />
                </div>
              </>
            ) : (
              <CheckLine data={data} oem={oem} />
            )}
            {!asking && lookup.state.phase === 'failed' && <span role="alert" className="mt-1 block text-[#B91C1C] dark:text-[#F87171]">{lookup.state.error}</span>}
            {!asking && note && <span className="mt-1 block text-[#92400E] dark:text-[#FBBF24]">{note}</span>}
          </div>
          {data && (
            <button type="button" className={buttonClass()} onClick={() => void askAgain()} disabled={asking || !client}>
              <Icon name="refresh" size={15} />
              {oem ? t('Ask {{oem}} again', { oem }) : t('Ask the OEM again')}
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

function CopyButton({ text }: { readonly text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <>
      <button
        type="button"
        aria-label={t('Copy serial')}
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined);
        }}
        className="cursor-pointer border-0 bg-transparent p-0.5 text-[#5C6B85] dark:text-[#8b949e] hover:text-[#0B1B3D] dark:hover:text-[#e6edf3]"
      >
        <Icon name="copy" size={15} />
      </button>
      <span aria-live="polite" className="text-xs text-[#047857] dark:text-[#34D399]">{copied ? t('Copied') : ''}</span>
    </>
  );
}

/** "Checked with HP on 4 Nov 2026 · from support.hp.com", then when it is asked again. */
function CheckLine({ data, oem }: { readonly data: ProductLookup | null; readonly oem: string | null }) {
  const { t } = useTranslation();
  const check = data?.check;
  if (!check) return null;
  const source = check.source_url;
  const imported = check.origin === 'file_import';
  let first = oem
    ? t('Checked with {{oem}} on {{date}}', { oem, date: shortDate(check.checked_at) })
    : t('Checked on {{date}}', { date: shortDate(check.checked_at) });
  if (imported) first = t('Imported on {{date}}', { date: shortDate(check.checked_at) });
  return (
    <>
      {first}
      {source ? (
        <>
          {' · '}{t('from')}{' '}
          {/^https?:\/\//.test(source)
            ? <a href={source} target="_blank" rel="noreferrer" className="text-[#2563EB] dark:text-[#58a6ff] hover:text-[#1D4ED8] dark:hover:text-[#79c0ff]">{sourceHost(source)}</a>
            : source}
        </>
      ) : null}
      {data?.next_check_after ? (
        <>
          <br />
          {t('Next automatic check after {{date}}', { date: shortDate(data.next_check_after) })}
        </>
      ) : null}
    </>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div>
      <div className={`${LABEL} mb-1`}>{label}</div>
      {children}
    </div>
  );
}

const VALUE = 'text-sm font-semibold text-[#0B1B3D] dark:text-[#e6edf3]';
const SECTION = 'm-0 mb-3 text-[11px] font-bold uppercase tracking-[.06em] text-[#5C6B85] dark:text-[#8b949e]';

function StoredUnit({ data, oem }: { readonly data: ProductLookup; readonly oem: string | null }) {
  const { t } = useTranslation();
  const warranty = data.warranty;
  const coverages = warranty?.coverages ?? [];
  const byStatus = coverages.reduce<Record<string, number>>((counts, coverage) => {
    counts[coverage.status] = (counts[coverage.status] ?? 0) + 1;
    return counts;
  }, {});
  const coverageText = [
    byStatus.active ? t('{{count}} active', { count: byStatus.active }) : null,
    byStatus.expired ? t('{{count}} expired', { count: byStatus.expired }) : null,
    byStatus.not_started ? t('{{count}} not started', { count: byStatus.not_started }) : null,
  ].filter(Boolean).join(' · ') || t('None reported');

  let warrantyText = t('No coverage reported');
  let warrantyNote: string | null = null;
  if (warranty?.status === 'active') {
    warrantyText = warranty.end_date ? t('Active until {{date}}', { date: warranty.end_date }) : t('Active');
    if (warranty.days_remaining !== null && warranty.days_remaining !== undefined) {
      warrantyNote = t('{{days}} days left', { days: warranty.days_remaining.toLocaleString() });
    }
  } else if (warranty?.status === 'expired') {
    warrantyText = warranty.end_date ? t('Expired on {{date}}', { date: warranty.end_date }) : t('Expired');
  } else if (warranty?.status === 'not_started') {
    warrantyText = warranty.start_date ? t('Starts {{date}}', { date: warranty.start_date }) : t('Not started');
  }

  return (
    <>
      <section aria-label={t('Summary')} className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-[#F7F8FA] dark:bg-[#161b22] p-4">
        <Field label={t('Warranty')}>
          <div className={VALUE}>{warrantyText}</div>
          {warrantyNote && <div className="mt-0.5 text-xs text-[#5C6B85] dark:text-[#8b949e]">{warrantyNote}</div>}
        </Field>
        <Field label={t('Product number')}>
          <div className={`${VALUE} ${MONO}`}>{data.product?.product_number || '—'}</div>
        </Field>
        <Field label={t('Coverages')}>
          <div className={VALUE}>{coverageText}</div>
        </Field>
        <Field label={t('Ship date')}>
          {data.unit?.ship_date
            ? <div className={VALUE}>{data.unit.ship_date}</div>
            : <div className={`${VALUE} !text-[#5C6B85] dark:!text-[#8b949e]`}>{oem ? t('Not reported by {{oem}}', { oem }) : t('Not reported')}</div>}
        </Field>
      </section>
      {coverages.length > 0 && (
        <section aria-label={t('Coverage timeline')}>
          <h3 className={SECTION}>{t('Coverage timeline')}</h3>
          <CoverageTimeline coverages={coverages} now={new Date()} />
        </section>
      )}
      {coverages.length > 0 && (
        <section aria-label={t('Coverages')}>
          <h3 className={SECTION}>{t('Coverages')}</h3>
          <div className="overflow-hidden rounded-xl border border-[#E5E7EB] dark:border-[#30363d]">
            {coverages.map((coverage, index) => (
              <CoverageItem key={coverage.external_id ?? `${coverage.name}-${index}`} coverage={coverage} open={index === 0} last={index === coverages.length - 1} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/** Bars per coverage on a year axis, with today marked. */
export function CoverageTimeline({ coverages, now }: { readonly coverages: readonly Coverage[]; readonly now: Date }) {
  const { t } = useTranslation();
  const timeline = buildTimeline(coverages, now);
  if (!timeline) return <p className="m-0 text-sm text-[#5C6B85] dark:text-[#8b949e]">{t('The OEM reported no coverage dates.')}</p>;
  const { years, bars, today, todayAlign } = timeline;
  const statuses = new Set(bars.map((bar) => bar.coverage.status));
  const todayText = t('Today · {{date}}', { date: shortDate(now) });
  const shift = { start: 'translateX(0)', center: 'translateX(-50%)', end: 'translateX(-100%)' }[todayAlign];
  const description = t('Coverages from {{from}} to {{to}}; today is {{today}}', {
    from: years[0]?.year,
    to: years[years.length - 1]?.year,
    today: shortDate(now),
  });

  return (
    <>
      <div className="flex gap-3">
        <div className="w-[180px] shrink-0">
          <div className="h-5" />
          {bars.map(({ coverage }, index) => (
            <div key={index} className="h-10 text-[13px] font-medium leading-[1.3] text-[#0B1B3D] dark:text-[#e6edf3]">
              <div className="truncate" title={coverage.name ?? undefined}>{coverage.name || t('Coverage')}</div>
              {coverage.coverage_type && <div className="truncate text-[11px] font-normal text-[#5C6B85] dark:text-[#8b949e]">{coverage.coverage_type}</div>}
            </div>
          ))}
          <div className="h-[22px]" />
        </div>
        <div role="img" aria-label={description} className="relative min-w-0 flex-1">
          <div className="relative h-5 text-[11px] text-[#5C6B85] dark:text-[#8b949e]">
            {years.map((year) => <span key={year.year} className="absolute" style={{ left: `${year.left}%` }}>{year.year}</span>)}
          </div>
          {bars.map((bar, index) => (
            <div key={index} className={`relative h-10 border-t border-[#F1F2F5] dark:border-[#21262d] ${index === bars.length - 1 ? 'border-b' : ''}`}>
              {bar.left !== null && bar.width !== null && (
                <span
                  data-bar={bar.coverage.status}
                  title={`${bar.coverage.name ?? ''}: ${bar.coverage.start_date} → ${bar.coverage.end_date}`}
                  className={`absolute top-2 h-3 min-w-1 rounded-md ${BAR_COLOURS[bar.coverage.status] ?? BAR_COLOURS.expired}`}
                  style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                />
              )}
            </div>
          ))}
          <div data-today="" className="absolute top-4 border-l-2 border-dashed border-[#D4017E] dark:border-[#F778BA]" style={{ left: `${today}%`, height: bars.length * 40 + 8 }} />
          <div className="relative h-[22px]">
            <span className="absolute top-1 whitespace-nowrap text-[11px] font-bold text-[#B8016D] dark:text-[#F778BA]" style={{ left: `${today}%`, transform: shift }}>
              {todayText}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-4 text-xs text-[#5C6B85] dark:text-[#8b949e]">
        <Legend colour={BAR_COLOURS.active} label={t('Active')} />
        <Legend colour={BAR_COLOURS.expired} label={t('Expired')} />
        {statuses.has('not_started') && <Legend colour={BAR_COLOURS.not_started} label={t('Not started')} />}
      </div>
    </>
  );
}

function Legend({ colour, label }: { readonly colour: string; readonly label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-[18px] rounded ${colour}`} />
      {label}
    </span>
  );
}

function CoverageItem({ coverage, open, last }: { readonly coverage: Coverage; readonly open: boolean; readonly last: boolean }) {
  const { t } = useTranslation();
  const [allLevels, setAllLevels] = useState(false);
  const badge = warrantyBadge(coverage.status);
  const levels = coverage.service_levels ?? [];
  const deliverables = coverage.deliverables ?? [];
  const shownLevels = allLevels ? levels : levels.slice(0, LEVELS_SHOWN);
  const details = Boolean(coverage.service_type || levels.length || deliverables.length);
  return (
    <details open={open} className={last ? '' : 'border-b border-[#E5E7EB] dark:border-[#30363d]'}>
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden">
        <span className="flex-1 text-sm font-semibold">
          {coverage.name || t('Coverage')}
          {coverage.coverage_type && <span className="font-normal text-[#5C6B85] dark:text-[#8b949e]"> · {coverage.coverage_type}</span>}
        </span>
        <span className="whitespace-nowrap text-xs text-[#5C6B85] dark:text-[#8b949e]">{coverage.start_date || '—'} → {coverage.end_date || '—'}</span>
        <Badge tone={badge.tone}>{t(coverage.status === 'unknown' ? 'Unknown' : badge.label)}</Badge>
      </summary>
      {details && (
        <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 pb-4 text-[13px]">
          {coverage.service_type && (
            <>
              <div className="text-[#5C6B85] dark:text-[#8b949e]">{t('Service')}</div>
              <div>{coverage.service_type}</div>
            </>
          )}
          {levels.length > 0 && (
            <>
              <div className="text-[#5C6B85] dark:text-[#8b949e]">{t('Service levels')}</div>
              <div>
                {shownLevels.join(' · ')}
                {levels.length > LEVELS_SHOWN && !allLevels && (
                  <> · <button type="button" className={LINK} onClick={() => setAllLevels(true)}>{t('+{{count}} more', { count: levels.length - LEVELS_SHOWN })}</button></>
                )}
              </div>
            </>
          )}
          {deliverables.length > 0 && (
            <>
              <div className="text-[#5C6B85] dark:text-[#8b949e]">{t('Deliverables')}</div>
              <div className="flex flex-wrap gap-1.5">
                {deliverables.map((item) => <Badge key={item} tone="neutral">{item}</Badge>)}
              </div>
            </>
          )}
        </div>
      )}
    </details>
  );
}

/** A serial that is checked but not stored: what the OEM said, and the model number when it asks for one. */
function NotStored({ data, oem, model, onModel, onAsk, busy }: {
  readonly data: ProductLookup;
  readonly oem: string | null;
  readonly model: string;
  readonly onModel: (value: string) => void;
  readonly onAsk: (modelNumber: string) => void;
  readonly busy: boolean;
}) {
  const { t } = useTranslation();
  const modelId = useId();
  const check = data.check;
  let why = t('Its warranty site has no record of this serial.');
  if (data.status === 'oem_unknown') why = t('The OEM could not be told from this serial.');
  if (data.status === 'unsupported') why = t('This OEM’s warranty site is not supported yet.');
  if (data.status === 'needs_more_info') why = t('Some serials are shared across models. Add the model number from the unit’s label and ask again.');
  if (data.status === 'error') why = check?.error?.message || check?.error?.code || t('The OEM site did not answer.');
  return (
    <>
      <section aria-label={t('Summary')} className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-[#F7F8FA] dark:bg-[#161b22] p-4">
        <Field label={t('Stored')}>
          <div className={VALUE}>{t('No product stored')}</div>
        </Field>
        <Field label={t('Last check')}>
          <div className={VALUE}>{shortDate(check?.checked_at)}</div>
        </Field>
        <div className="col-span-2">
          <Field label={t('What the OEM said')}>
            <div className="text-sm text-[#0B1B3D] dark:text-[#e6edf3]">{why}</div>
          </Field>
        </div>
      </section>
      {data.status === 'needs_more_info' && (
        <form
          className="flex flex-wrap items-end gap-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            if (model.trim()) onAsk(model.trim());
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor={modelId} className="text-xs font-semibold text-[#434A59] dark:text-[#c9d1d9]">{t('Model number')}</label>
            <input
              id={modelId}
              className={`${FIELD} w-[260px]`}
              type="text"
              placeholder={t('e.g. as printed on the label')}
              value={model}
              onChange={(event) => onModel(event.target.value)}
            />
          </div>
          <button type="submit" className={buttonClass({ primary: true })} disabled={busy || !model.trim()}>
            {oem ? t('Ask {{oem}} with the model number', { oem }) : t('Ask with the model number')}
          </button>
        </form>
      )}
    </>
  );
}
